import { useState } from "react";

export default function Footer() {
  const [formStatus, setFormStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) {
      alert("Message must be 500 characters or less.");
      return;
    }
    setFormStatus("sending");
    try {
      const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
      const res = await fetch("https://readdy.ai/api/form/d7bfnsrurf8q17puvv30", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setFormStatus("sent");
        form.reset();
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    }
  };

  return (
    <footer id="contact" className="w-full" style={{ backgroundColor: "#0f2a2a" }}>

      {/* ── Top CTA Banner ── */}
      <div className="w-full py-16 px-6 md:px-10 border-b border-white/10">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-8">
          <div>
            <p className="text-xs uppercase tracking-[3px] text-cyan-400 font-semibold mb-3">
              Ready to Dive?
            </p>
            <h2 className="text-3xl md:text-4xl font-black text-white leading-tight">
              The Outer Reef Is Waiting.<br />
              <span className="text-cyan-300">Your Spot on MV Mahi-Mahi Won&apos;t Be.</span>
            </h2>
            <p className="text-white/60 text-sm mt-3 max-w-md leading-relaxed">
              Scuba diving from Bedarra Island doesn&apos;t get easier than this — book direct, skip the middleman, and get on the water.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 shrink-0">
            <a
              href="https://wa.me/61455957263?text=Hi!%20I'd%20like%20to%20book%20a%20dive%20trip%20to%20Bedarra%20Island."
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-sm cursor-pointer hover:scale-105 transition-transform duration-300"
              style={{ boxShadow: "0 8px 28px rgba(249,115,22,0.4)" }}
            >
              <i className="ri-calendar-check-line w-4 h-4 flex items-center justify-center" />
              Book Your Dive Day Trip
            </a>
            <a
              href="tel:0455957263"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border-2 border-white/30 text-white font-semibold text-sm cursor-pointer hover:border-white/70 hover:bg-white/10 transition-all duration-200"
            >
              <i className="ri-phone-line w-4 h-4 flex items-center justify-center" />
              0455 957 263
            </a>
          </div>
        </div>
      </div>

      {/* ── Main Footer Body ── */}
      <div className="w-full py-16 px-6 md:px-10">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">

          {/* Left — Map + Contact Details */}
          <div className="flex flex-col gap-8">
            {/* Google Map embed */}
            <div className="w-full h-64 rounded-2xl overflow-hidden border border-white/10">
              <iframe
                title="Mission Beach Dive — Shop 16 The Village Green Mission Beach"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3827.4!2d146.1036!3d-17.8614!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sShop%2016%20The%20Village%20Green%2C%2045%20Porters%20Promenade%2C%20Mission%20Beach%20QLD%204852!5e0!3m2!1sen!2sau!4v1700000000001!5m2!1sen!2sau"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                decoding="async"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            {/* Contact details */}
            <div className="flex flex-col gap-5">
              <img
                src="https://storage.readdy-site.link/project_files/c46f7f07-593f-4f17-9a57-3ac86083c18c/8ad1d54c-f962-4091-84fc-ce9d0111f123_1-4.png?v=f770e7fb4e5aaa93207d098e21607861"
                alt="Mission Beach Dive Adventures"
                className="h-14 w-auto object-contain"
                width="280"
                height="56"
                loading="lazy"
                decoding="async"
                style={{ filter: "brightness(0) invert(1)" }}
              />

              <div className="flex items-start gap-3 text-white/70 text-sm">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 text-cyan-400 shrink-0 mt-0.5">
                  <i className="ri-map-pin-2-line w-4 h-4 flex items-center justify-center" />
                </div>
                <div>
                  <p className="text-white/90 font-medium">Shop 16, The Village Green</p>
                  <p>45 Porters Promenade, Mission Beach QLD 4852</p>
                  <p>Australia</p>
                </div>
              </div>

              <a href="tel:0455957263" className="flex items-center gap-3 text-white/70 text-sm hover:text-cyan-300 transition-colors cursor-pointer group">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 text-cyan-400 shrink-0 group-hover:bg-cyan-400/20">
                  <i className="ri-phone-line w-4 h-4 flex items-center justify-center" />
                </div>
                <span>0455 957 263</span>
              </a>

              <a href="mailto:info@missionbeachdive.com.au" className="flex items-center gap-3 text-white/70 text-sm hover:text-cyan-300 transition-colors cursor-pointer group">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 text-cyan-400 shrink-0 group-hover:bg-cyan-400/20">
                  <i className="ri-mail-line w-4 h-4 flex items-center justify-center" />
                </div>
                <span>info@missionbeachdive.com.au</span>
              </a>

              <a
                href="https://www.missionbeachdive.com.au"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-white/70 text-sm hover:text-cyan-300 transition-colors cursor-pointer group"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 text-cyan-400 shrink-0 group-hover:bg-cyan-400/20">
                  <i className="ri-global-line w-4 h-4 flex items-center justify-center" />
                </div>
                <span>missionbeachdive.com.au</span>
              </a>

              {/* Hours / Departures */}
              <div className="flex items-start gap-3 text-white/70 text-sm">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 text-cyan-400 shrink-0 mt-0.5">
                  <i className="ri-time-line w-4 h-4 flex items-center justify-center" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <p className="text-white/90 font-medium">Departures &amp; Office Hours</p>
                  <div className="flex flex-col gap-1 text-white/60 text-xs leading-relaxed">
                    <div className="flex items-center justify-between gap-6">
                      <span>Daily departures</span>
                      <span className="text-white/80 font-medium">7:30 am</span>
                    </div>
                    <div className="flex items-center justify-between gap-6">
                      <span>Return to jetty</span>
                      <span className="text-white/80 font-medium">~3:30 pm</span>
                    </div>
                    <div className="mt-1.5 pt-1.5 border-t border-white/10 flex items-center justify-between gap-6">
                      <span>Office / phone</span>
                      <span className="text-white/80 font-medium">7 am – 5 pm daily</span>
                    </div>
                  </div>
                  <p className="text-white/40 text-[11px] mt-1">
                    Clump Point Jetty, Mission Beach · Check-in 30 min before departure
                  </p>
                </div>
              </div>

              {/* Social links */}
              <div className="flex items-center gap-3 pt-2">
                <a
                  href="https://www.facebook.com/missionbeachdive"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-white/60 hover:bg-cyan-400/20 hover:text-cyan-300 transition-all cursor-pointer"
                >
                  <i className="ri-facebook-fill w-4 h-4 flex items-center justify-center" />
                </a>
                <a
                  href="https://www.instagram.com/missionbeachdive"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-white/60 hover:bg-cyan-400/20 hover:text-cyan-300 transition-all cursor-pointer"
                >
                  <i className="ri-instagram-line w-4 h-4 flex items-center justify-center" />
                </a>
              </div>
            </div>
          </div>

          {/* Right — Enquiry Form */}
          <div>
            <h3 className="text-white font-bold text-lg mb-2">Send Us an Enquiry</h3>
            <p className="text-white/50 text-sm mb-6 leading-relaxed">
              Not ready to book online? Drop us a message and we&apos;ll get back to you fast — usually same day.
            </p>

            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-white/60 text-xs uppercase tracking-wider">Name</label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="Your name"
                    className="bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-cyan-400 transition-colors"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-white/60 text-xs uppercase tracking-wider">Email</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="your@email.com"
                    className="bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-cyan-400 transition-colors"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-white/60 text-xs uppercase tracking-wider">Experience Level</label>
                <select
                  name="experience_level"
                  className="bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-cyan-400 transition-colors cursor-pointer"
                >
                  <option value="" className="text-gray-800">Select your level</option>
                  <option value="Never dived before" className="text-gray-800">Never dived before</option>
                  <option value="Open Water certified" className="text-gray-800">Open Water certified</option>
                  <option value="Advanced Open Water" className="text-gray-800">Advanced Open Water</option>
                  <option value="Divemaster / Instructor" className="text-gray-800">Divemaster / Instructor</option>
                  <option value="Snorkel only" className="text-gray-800">Snorkel only</option>
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-white/60 text-xs uppercase tracking-wider">
                  Message <span className="normal-case text-white/30">(max 500 chars)</span>
                </label>
                <textarea
                  name="message"
                  rows={4}
                  maxLength={500}
                  placeholder="Preferred dates, group size, any questions..."
                  className="bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-cyan-400 transition-colors resize-none"
                />
              </div>

              {formStatus === "sent" ? (
                <div className="flex items-center gap-3 bg-emerald-500/20 border border-emerald-400/30 rounded-xl px-5 py-4 text-emerald-300 text-sm font-medium">
                  <i className="ri-checkbox-circle-line w-5 h-5 flex items-center justify-center" />
                  Message sent! We&apos;ll be in touch soon.
                </div>
              ) : (
                <button
                  type="submit"
                  disabled={formStatus === "sending"}
                  className="whitespace-nowrap inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-sm cursor-pointer hover:scale-105 transition-transform duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                  style={{ boxShadow: "0 8px 24px rgba(249,115,22,0.35)" }}
                >
                  <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center" />
                  {formStatus === "sending" ? "Sending..." : "Send Enquiry"}
                </button>
              )}

              {formStatus === "error" && (
                <p className="text-red-400 text-xs text-center">
                  Something went wrong — please try again or call us directly.
                </p>
              )}
            </form>
          </div>

        </div>
      </div>

      {/* ── Bottom Bar ── */}
      <div className="w-full border-t border-white/10 py-6 px-6 md:px-10">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-white/30 text-xs">
          <div className="flex items-center gap-3">
            <img
              src="https://storage.readdy-site.link/project_files/c46f7f07-593f-4f17-9a57-3ac86083c18c/8ad1d54c-f962-4091-84fc-ce9d0111f123_1-4.png?v=f770e7fb4e5aaa93207d098e21607861"
              alt="Mission Beach Dive Adventures"
              className="h-7 w-auto object-contain opacity-40"
              width="140"
              height="28"
              loading="lazy"
              decoding="async"
              style={{ filter: "brightness(0) invert(1)" }}
            />
            <span>&copy; {new Date().getFullYear()} All rights reserved.</span>
          </div>
          <p>PADI Dive Centre · Shop 16, The Village Green, Mission Beach QLD · Great Barrier Reef Marine Park Permitted</p>
        </div>
      </div>

    </footer>
  );
}
